import { Typography } from "@mui/joy";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useApiService from "../../../../hooks/useApiService";
import { Card } from "primereact/card";
import { RouteComponentProps } from "../../../../models/RouteComponent";
import styles from "../styles.module.css";
import { formatDateTime } from "../../../../utils/DateUtils";
import {
  CheckIcon,
  CircleBlock,
  CircleWarning,
  DescriptionDocument,
} from "../../../../assets/icons";
import { StatusBadge } from "../../../../components/StatusBadge/StatusBadge";
import { capitaliseEveryFirstLetter, snakeToKey } from "../../../../utils/generalUtils";
import { checklistSubmittedDetails } from "../../../../models/checklists";

function ChecklistSubmissionDetails(props: RouteComponentProps) {
  const { id } = useParams<{ id: string }>();
  const [groupedResponses, setGroupedResponses] = useState<{
    [key: string]: checklistSubmittedDetails["responses"];
  }>({});

  const { loading, data } = useApiService<checklistSubmittedDetails>({
    url: `checklist-submission/${id}`,
    method: "GET",
    apiName: "core",
    onSuccess: data => {
      // group responses by category_name
      const groupedResponses: { [key: string]: checklistSubmittedDetails["responses"] } = {};
      data.responses.forEach(response => {
        if (!groupedResponses[response.category_name]) {
          groupedResponses[response.category_name] = [];
        }
        groupedResponses[response.category_name].push(response);
      });
      setGroupedResponses(groupedResponses);
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loading);
  }, [loading, props]);

  return (
    <>
      <Typography level="h2" component="h1">
        Checklist Submission
      </Typography>
      <Card title={data?.template_name} subTitle={data?.template_description}>
        <div className={styles["grid-3"]}>
          <div className={styles["info-block"]}>
            <span>Executed by:</span>
            <span>{data?.executor_name + " " + data?.executor_surname}</span>
          </div>
          <div className={styles["info-block"]}>
            <span>Site:</span>
            <span>{data?.site_name}</span>
          </div>
          <div className={styles["info-block"]}>
            <span>Executed at:</span>
            <span>{formatDateTime(data?.created_at, "long")}</span>
          </div>
        </div>
      </Card>
      <div className={`${styles["grid-4"]} ${styles["info-section"]}`}>
        <Card>
          <div
            className={styles[`info-highlight-${data?.overall_compliance ? "success" : "error"}`]}
          >
            <div>
              <span>Overall compliance </span>
              <span>{data?.compliance_percentage} %</span>
            </div>
            <div>
              {data?.overall_compliance ? (
                <CheckIcon fill={"#028748FF"} height={28} width={28} />
              ) : (
                <CircleWarning fill={"#b90000"} height={28} width={28} />
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className={styles["info-highlight"]}>
            <div>
              <span>Total questions</span>
              <span>{data?.responses.length}</span>
            </div>
            <div>
              <DescriptionDocument fill={"#37383b"} height={28} width={28} />
            </div>
          </div>
        </Card>
        <Card>
          <div className={styles["info-highlight-success"]}>
            <div>
              <span>Compliant</span>
              <span>
                {data?.responses.filter(response => response.response_type === "compliant").length}
              </span>
            </div>
            <div>
              <CheckIcon fill={"#028748FF"} height={28} width={28} />
            </div>
          </div>
        </Card>
        <Card>
          <div className={styles["info-highlight-error"]}>
            <div>
              <span>Non-Compliant</span>
              <span>
                {
                  data?.responses.filter(response => response.response_type === "non_compliant")
                    .length
                }
              </span>
            </div>
            <div>
              <CircleWarning fill={"#b90000"} height={28} width={28} />
            </div>
          </div>
        </Card>
      </div>
      <Card title={"Submission Notes"} subTitle={data?.notes} />
      <div className={styles["responses-section"]}>
        {Object.keys(groupedResponses).map((key, index) => {
          const responses = groupedResponses[key];

          return (
            <Card key={index} title={key}>
              {responses.map(response => {
                return (
                  <div key={response.id} className={styles["item"]}>
                    <div key={response.id} className={styles[`response-item`]}>
                      {response.response_type === "compliant" ? (
                        <CheckIcon fill={"#028748FF"} height={28} width={28} />
                      ) : response.response_type === "non_compliant" ? (
                        <CircleWarning fill={"#b90000"} height={28} width={28} />
                      ) : (
                        <CircleBlock fill={"#37383b"} height={28} width={28} />
                      )}
                      <span className={styles["response-question"]}>{response.question_text}</span>
                      <StatusBadge
                        variant={
                          response.response_type === "compliant"
                            ? "success"
                            : response.response_type === "non_compliant"
                              ? "error"
                              : "info"
                        }
                      >
                        {capitaliseEveryFirstLetter(snakeToKey(response.response_type))}
                      </StatusBadge>
                      {response.required && <StatusBadge variant={"medium"}>Required</StatusBadge>}
                    </div>
                    {response.non_compliant_option_text && (
                      <div className={styles["info-block"]} style={{ marginLeft: "2.75rem" }}>
                        <span>Non compliant category:</span>
                        <span>{response.non_compliant_option_text}</span>
                      </div>
                    )}

                    {response.corrective_actions && response.corrective_actions.length > 0 && (
                      <div
                        className={styles["info-block-list"]}
                        style={{ marginLeft: "2.75rem", marginTop: "1rem" }}
                      >
                        <span style={{ marginBottom: "-0.75rem" }}>Corrective actions:</span>
                        <ul>
                          {response.corrective_actions.map((action, actionIndex) => (
                            <li key={actionIndex}>{action.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {response.other_corrective_action && (
                      <div className={styles["info-block-small"]} style={{ marginLeft: "2.75rem" }}>
                        <span>Other corrective action:</span>
                        <span>{response.other_corrective_action}</span>
                      </div>
                    )}
                    {response.notes && (
                      <div
                        className={styles["info-block"]}
                        style={{ marginLeft: "2.75rem", marginTop: "1rem" }}
                      >
                        <span>Notes:</span>
                        <span>{response.notes}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          );
        })}
      </div>
    </>
  );
}

export default ChecklistSubmissionDetails;
